import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ProductImages from "../../../../components/listing/categorydetailproperty/ProductImages";
import ProductInfo from "../../../../components/listing/categorydetailproperty/ProductInfo";
import SellerInfo from "../../../../components/listing/categorydetailproperty/SellerInfo";
import ProductComment from "../../../../components/listing/comment/ProductComment";
import ProductGrid from "../../../../components/listing/product-card/ProductGrid";
import "./ProductDetailPageNew.css";

const ProductDetailPageNew = () => {
  const { productID } = useParams();
  const [product, setProduct] = useState(null);
  const [userListings, setUserListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);

        // Fetch product detail
        const productResponse = await fetch(
          `https://iudi-api.thinkdiff.us/api/listings/${productID}`
        );
        const productData = await productResponse.json();

        if (!productResponse.ok) {
          throw new Error("Không tìm thấy sản phẩm");
        }

        setProduct(productData);

        // Fetch user listings if user ID exists
        if (productData.id_user_post) {
          try {
            const userListingsResponse = await fetch(
              `https://iudi-api.thinkdiff.us/api/listings_by_user/${productData.id_user_post}?page=1`
            );
            const userListingsData = await userListingsResponse.json();
            setUserListings(userListingsData.data || []);
          } catch (err) {
            console.warn("Không thể tải danh sách sản phẩm của người bán:", err);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message || "Không thể tải dữ liệu sản phẩm");
        setLoading(false);
      }
    };

    if (productID) {
      fetchProductData();
    }
  }, [productID]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("vi-VN");
  };

  if (loading) {
    return (
      <div className="product-detail-page-new">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải thông tin sản phẩm...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-page-new">
        <div className="error-container">
          <h2>Lỗi</h2>
          <p>{error}</p>
          <Link to="/market/thanh-ly" className="back-btn">
            Quay lại
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page-new">
        <div className="error-container">
          <h2>Không tìm thấy sản phẩm</h2>
          <p>Sản phẩm này có thể đã bị xóa hoặc không tồn tại.</p>
          <Link to="/market/thanh-ly" className="back-btn">
            Quay lại
          </Link>
        </div>
      </div>
    );
  }

  // Mock images nếu không có
  const productImages = product.images || [
    product.avatar_link || "https://via.placeholder.com/500x400/1e293b/4ade80?text=Product+Image"
  ];

  return (
    <div className="product-detail-page-new">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/market" className="breadcrumb-link">
          Trang chủ
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/market/thanh-ly" className="breadcrumb-link">
          Thanh lý
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{product.title}</span>
      </div>

      {/* Row 1: Product Images + Product Info + Seller Info */}
      <div className="row-1">
        <div className="column-left">
          <ProductImages 
            images={productImages}
            mainImage={product.avatar_link}
          />
        </div>
        <div className="column-right">
          <div className="row-top">
            <ProductInfo product={product} />
          </div>
          <div className="row-bottom">
            <SellerInfo seller={product} />
          </div>
        </div>
      </div>

      {/* Row 2: Product Description + Comments */}
      <div className="row-2">
        <div className="column-left">
          <div className="product-description-section">
            <h3 className="section-title">📝 Mô tả chi tiết</h3>
            <div className="description-content">
              {product.Description ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: product.Description.replace(/\n/g, "<br/>"),
                  }}
                />
              ) : (
                <p>Chưa có mô tả chi tiết</p>
              )}
            </div>

            {/* Hashtags */}
            {product.Hashtags && product.Hashtags.length > 0 && (
              <div className="hashtags-section">
                <h4>Tags</h4>
                <div className="hashtags">
                  {product.Hashtags.map((tag, index) => (
                    <span key={index} className="hashtag">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Product Specifications */}
            <div className="specifications-section">
              <h4>Thông số kỹ thuật</h4>
              <div className="specifications">
                <div className="spec-item">
                  <span className="spec-label">Mã sản phẩm:</span>
                  <span className="spec-value">{product.ListingID}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Danh mục:</span>
                  <span className="spec-value">{product.CategoryName}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Địa chỉ:</span>
                  <span className="spec-value">{product.Address}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Quận/Huyện:</span>
                  <span className="spec-value">{product.DistrictName}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Ngày đăng:</span>
                  <span className="spec-value">
                    {formatDate(product.created_time)}
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Cập nhật lần cuối:</span>
                  <span className="spec-value">
                    {formatDate(product.updated_time)}
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Trạng thái:</span>
                  <span className="spec-value">
                    {product.status || "Đang bán"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="column-right">
          <ProductComment listingId={productID} />
        </div>
      </div>

      {/* Row 3: Other Products from Seller */}
      <div className="row-3">
        <div className="other-products-section">
          <h3 className="section-title">
            📦 Tin khác của người đăng ({userListings.length})
          </h3>
          {userListings.length > 0 ? (
            <ProductGrid listings={userListings.slice(0, 6)} useNewCard={true} />
          ) : (
            <div className="no-products">
              <div className="no-products-icon">📦</div>
              <p>Người bán này chưa có sản phẩm khác</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPageNew;
